import React, { memo } from "react";
import { UseFormReturn, FieldValues, useWatch, Path, FieldErrors } from "react-hook-form";
import { GenericEntity, ICustomTypedEntity } from "../../../../api/GenericTypes";
import { checkIsDirty } from "../common/MultiEditUtils";
import { MultiEditFormWrapper } from "../common/MultiEditFormWrapper";
import { MultiEditCustomFieldFormProps } from "../common/MultiEditSelectFormTypes";
import { customFieldToCustomFieldForm } from "../../../../Customization/CustomFields/CustomFieldUtils";
import { CustomFieldEditRenderer } from "../../../../Customization/CustomFields/CustomFieldEditRenderer";

const MemoComponent = memo(
  <Entity extends GenericEntity & Partial<ICustomTypedEntity>>({
    control,
    register,
    formState,
    getFieldState,
    row,
    customField,
    types,
    disabled,
    ...rest
  }: UseFormReturn<FieldValues> & MultiEditCustomFieldFormProps<Entity>) => {
    // const [_isError, setIsError] = useState<FieldError | undefined>();
    // const [_isDirty, setIsDirty] = useState<boolean>(false);

    // const onBlur = useCallback(() => {
    //   setIsDirty(checkIsDirty(formState.dirtyFields?.[row.id]?.customFields?.[customField.id]));
    //   setIsError(getFieldState(`${row.id}.customFields.${customField.id}`, formState).error);
    // }, [customField.id, formState, getFieldState, row.id]);

    const isDirty = checkIsDirty(formState.dirtyFields?.[row.id]?.customFields?.[customField.id]);
    const isError = getFieldState(`${row.id}.customFields.${customField.id}`, formState).error;
    const _customType = useWatch({ name: `${row.id}.customType`, control: control });

    if (!_customType) {
      return (
        <div
          className="flex align-center justify-center"
          style={{
            width: "100%",
            height: "100%",
            background: `
              repeating-linear-gradient(
          45deg,
          var(--gray-100),
          var(--gray-100) 10px,
          var(--gray-200) 10px,
          var(--gray-200) 20px
              )
            `,
            color: "var(--gray-500)",
            fontStyle: "italic",
            opacity: 0.8,
          }}
          title="This entity does not have a custom type, therefore its custom fields cannot be edited. Please select a custom type to edit this field."
        >
          N/A
        </div>
      );
    }

    if (!Object.hasOwn(types, _customType.id)) {
      return (
        <div
          className="flex align-center justify-center"
          style={{
            width: "100%",
            height: "100%",
            background: `
              repeating-linear-gradient(
          45deg,
          var(--gray-100),
          var(--gray-100) 10px,
          var(--gray-200) 10px,
          var(--gray-200) 20px
              )
            `,
            color: "var(--gray-500)",
            fontStyle: "italic",
            opacity: 0.8,
          }}
          title="This entity does not have a custom type, therefore its custom fields cannot be edited. Please select a custom type to edit this field."
        >
          N/A
        </div>
      );
    }

    if (
      Object.hasOwn(types, _customType.id) &&
      !types[_customType.id].sections
        .map((s) => s.customFields.map((c) => c.id))
        .flat(Infinity)
        .includes(customField.id)
    ) {
      return (
        <div
          className="flex align-center justify-center"
          style={{
            width: "100%",
            height: "100%",
            background: `
              repeating-linear-gradient(
          45deg,
          var(--gray-100),
          var(--gray-100) 10px,
          var(--gray-200) 10px,
          var(--gray-200) 20px
              )
            `,
            color: "var(--gray-500)",
            fontStyle: "italic",
            opacity: 0.8,
          }}
          title="This custom field is not part of the selected custom type."
        >
          N/A
        </div>
      );
    }
    const _customField = customFieldToCustomFieldForm(customField);
    return (
      <CustomFieldEditRenderer<Entity>
        id={`${row.id}.customFields.${customField.id}` as Path<Entity>}
        label={""}
        control={control as any}
        errors={formState.errors as FieldErrors<Entity>}
        placeholder={_customField.placeholder ?? ""}
        required={_customField.required}
        defaultValue={_customField.defaultValues}
        disabled={disabled || _customField.readOnly}
        dataType={_customField.dataType!}
        predefinedOptions={_customField.predefinedOptions}
        showAsTextArea={_customField.showAsTextArea}
        description={_customField.description}
        isMulti={!!_customField.isMulti}
        customFieldId={customField.id}
        predefinedOptionsFromValues={customField.predefinedOptionsFromValues}
        filters={
          Array.isArray(customField.customTypeConstraint) && !!customField.customTypeConstraint.length
            ? { customTypeIds: customField.customTypeConstraint.map((c) => c.id) }
            : { customTypeIds: null }
        }
        style={{
          width: "100%",
          height: "100%",
          border: isError ? "1px solid var(--danger)" : isDirty ? "1px solid var(--success)" : "0px",
          borderRadius: 4,
          background: disabled
            ? "var(--gray-100)"
            : isError
              ? "var(--danger-light)"
              : isDirty
                ? "var(--success-light)"
                : "unset",
        }}
        uncontained
      />
    );
  },
  (prevProps, nextProps) =>
    checkIsDirty(prevProps.formState.dirtyFields?.[prevProps.row.id]?.customFields?.[prevProps.customField.id]) ===
    checkIsDirty(nextProps.formState.dirtyFields?.[nextProps.row.id]?.customFields?.[prevProps.customField.id])
);

export const MultiEditCustomFieldForm = <Entity extends GenericEntity & Partial<ICustomTypedEntity>>(
  props: MultiEditCustomFieldFormProps<Entity>
) => {
  return <MultiEditFormWrapper>{(formProps) => <MemoComponent {...formProps} {...props} />}</MultiEditFormWrapper>;
};
